export interface OnboardingErrorItem
{
    code: string;
    message?: string;
}

export interface OnboardingEnvelope<T>
{
    success: boolean;
    data: T | null;
    errors: OnboardingErrorItem[];
}

export interface PlanOnboardingStepDtoWire
{
    Order: number;
    Key: string;
    Label: string | null;
    Applicable: boolean;
    Completed: boolean;
    Enabled: boolean;
    IsCurrent: boolean;
}

export interface PlanOnboardingStateDtoWire
{
    CurrentStep: string;
    NextStep: string | null;
    IsSubmittedToVerification: boolean;
    IsUnderReview: boolean;
    IsUnderReviewMta: boolean;
    IncidenceDescription: string | null;
    Steps: PlanOnboardingStepDtoWire[];
}

export interface RiskProfileSummaryDtoWire
{
    /** Finametrix _id del perfil de riesgo. */
    Id: string;
    Name: string;
    Order: number;
}

export interface CompatibleAlternativeDtoWire
{
    Id: number;
    Name: string;
    /** Finametrix _id del perfil de riesgo de la alternativa. */
    RiskProfile: string;
    RiskProfileName: string;
    RiskProfileOrder: number;
}

export interface PlanRiskProfileEvaluationDtoWire
{
    IsCompatible: boolean;
    IsApplicable: boolean;
    PlanRiskProfile: RiskProfileSummaryDtoWire | null;
    IntervenersRiskProfile: RiskProfileSummaryDtoWire | null;
    CompatibleAlternatives: CompatibleAlternativeDtoWire[];
}

export interface OnboardingEnvelopeWire<T>
{
    Success: boolean;
    Data: T | null;
    Errors: Array<{ Code: string; Message?: string }> | null;
}

export interface PlanOnboardingStep
{
    order: number;
    key: string;
    label: string | null;
    applicable: boolean;
    completed: boolean;
    enabled: boolean;
    isCurrent: boolean;
}

export interface PlanOnboardingState
{
    currentStep: string;
    nextStep: string | null;
    isSubmittedToVerification: boolean;
    isUnderReview: boolean;
    isUnderReviewMta: boolean;
    /** Texto de la incidencia registrada por el admin; solo llega en 3.2 / 3.3. */
    incidenceDescription: string | null;
    steps: PlanOnboardingStep[];
}

export interface RiskProfileSummary
{
    /** Finametrix _id del perfil de riesgo. */
    id: string;
    name: string;
    order: number;
}

export interface CompatibleAlternative
{
    id: number;
    name: string;
    /** Finametrix _id del perfil de riesgo de la alternativa. */
    riskProfile: string;
    riskProfileName: string;
    riskProfileOrder: number;
}

export interface PlanRiskProfileEvaluation
{
    isCompatible: boolean;
    isApplicable: boolean;
    planRiskProfile: RiskProfileSummary | null;
    intervenersRiskProfile: RiskProfileSummary | null;
    compatibleAlternatives: CompatibleAlternative[];
}

/** Identidad del catálogo de GET /savingsPlans/{id}/identitiesSelection. */
export interface IdentitiesSelectionIdentity
{
    id: number;
    firstName: string;
    lastName: string;
    secondLastName: string | null;
    email: string;
    /** Identidad principal del usuario: debe estar siempre incluida en el contrato. */
    isMainIdentity: boolean;
    isMinor: boolean;
    hasValidKyc: boolean;
    /**
     * El estado del interviniente permite darle un rol. Un borrador NEW no está sincronizado, así que
     * la formalización fallaría después de firmar. Se pinta en la tabla, pero sin roles.
     */
    isSelectable: boolean;
    /** true = prestado con KYC caducado (no seleccionable, se puede notificar). */
    canNotifyKycOwner: boolean | null;
}

/** Asignación (SPUI) del plan en identitiesSelection. */
export interface IdentitiesSelectionAssignment
{
    id: number;
    userIdentityId: number;
    savingsPlanId: number;
    relation: number;
}

export interface IdentitiesSelectionData
{
    savingsPlanId: number;
    savingsPlanName: string;
    userIdentities: IdentitiesSelectionIdentity[];
    assignments: IdentitiesSelectionAssignment[];
}

/**
 * Lee una propiedad aceptando PascalCase o camelCase (proxy/local puede variar).
 */
function readProp<T>(obj: any, pascalKey: string, camelKey: string): T | undefined
{
    if(obj == null || typeof obj !== "object")
    {
        return undefined;
    }

    if(pascalKey in obj && obj[pascalKey] !== undefined)
    {
        return obj[pascalKey] as T;
    }

    if(camelKey in obj)
    {
        return obj[camelKey] as T;
    }

    return undefined;
}

function normalizeRiskProfileSummary(wire: any): RiskProfileSummary | null
{
    if(!wire)
    {
        return null;
    }

    return{
        id: String(readProp(wire, "Id", "id") ?? ""),
        name: readProp<string>(wire, "Name", "name") as string,
        order: readProp<number>(wire, "Order", "order") as number,
    };
}

export function normalizePlanOnboardingState(wire: PlanOnboardingStateDtoWire | Record<string, unknown>): PlanOnboardingState
{
    const stepsRaw = readProp<any[]>(wire, "Steps", "steps") || [];

    return{
        currentStep: readProp<string>(wire, "CurrentStep", "currentStep") as string,
        nextStep: readProp<string | null>(wire, "NextStep", "nextStep") ?? null,
        isSubmittedToVerification: Boolean(readProp(wire, "IsSubmittedToVerification", "isSubmittedToVerification")),
        isUnderReview: Boolean(readProp(wire, "IsUnderReview", "isUnderReview")),
        isUnderReviewMta: Boolean(readProp(wire, "IsUnderReviewMta", "isUnderReviewMta")),
        incidenceDescription: readProp<string | null>(wire, "IncidenceDescription", "incidenceDescription") ?? null,
        steps: stepsRaw.map((s) =>
        ({
            order: readProp<number>(s, "Order", "order") as number,
            key: readProp<string>(s, "Key", "key") as string,
            label: readProp<string | null>(s, "Label", "label") ?? null,
            applicable: Boolean(readProp(s, "Applicable", "applicable")),
            completed: Boolean(readProp(s, "Completed", "completed")),
            enabled: Boolean(readProp(s, "Enabled", "enabled")),
            isCurrent: Boolean(readProp(s, "IsCurrent", "isCurrent")),
        })),
    };
}

export function normalizeRiskProfileEvaluation(wire: PlanRiskProfileEvaluationDtoWire | Record<string, unknown>): PlanRiskProfileEvaluation
{
    const alternativesRaw = readProp<any[]>(wire, "CompatibleAlternatives", "compatibleAlternatives") || [];

    return{
        isCompatible: Boolean(readProp(wire, "IsCompatible", "isCompatible")),
        isApplicable: Boolean(readProp(wire, "IsApplicable", "isApplicable")),
        planRiskProfile: normalizeRiskProfileSummary(readProp(wire, "PlanRiskProfile", "planRiskProfile")),
        intervenersRiskProfile: normalizeRiskProfileSummary(readProp(wire, "IntervenersRiskProfile", "intervenersRiskProfile")),
        compatibleAlternatives: alternativesRaw.map((a) =>
        ({
            id: readProp<number>(a, "Id", "id") as number,
            name: readProp<string>(a, "Name", "name") as string,
            riskProfile: String(readProp(a, "RiskProfile", "riskProfile") ?? ""),
            riskProfileName: readProp<string>(a, "RiskProfileName", "riskProfileName") as string,
            riskProfileOrder: readProp<number>(a, "RiskProfileOrder", "riskProfileOrder") as number,
        })),
    };
}

export function normalizeIdentitiesSelection(wire: Record<string, unknown>): IdentitiesSelectionData
{
    const identitiesRaw = readProp<any[]>(wire, "UserIdentities", "userIdentities") || [];
    const assignmentsRaw = readProp<any[]>(wire, "Assignments", "assignments") || [];

    return{
        savingsPlanId: readProp<number>(wire, "SavingsPlanId", "savingsPlanId") as number,
        savingsPlanName: (readProp<string>(wire, "SavingsPlanName", "savingsPlanName") || "") as string,
        userIdentities: identitiesRaw.map((identity) =>
        ({
            id: readProp<number>(identity, "Id", "id") as number,
            firstName: (readProp<string>(identity, "FirstName", "firstName") || "") as string,
            lastName: (readProp<string>(identity, "LastName", "lastName") || "") as string,
            secondLastName: readProp<string | null>(identity, "SecondLastName", "secondLastName") ?? null,
            email: (readProp<string>(identity, "Email", "email") || "") as string,
            isMainIdentity: Boolean(readProp(identity, "IsMainIdentity", "isMainIdentity")),
            isMinor: Boolean(readProp(identity, "IsMinor", "isMinor")),
            hasValidKyc: Boolean(readProp(identity, "HasValidKyc", "hasValidKyc")),
            // Ausente en backends anteriores al campo: se asume seleccionable para no vaciar la tabla.
            isSelectable: readProp<boolean>(identity, "IsSelectable", "isSelectable") !== false,
            canNotifyKycOwner: readProp<boolean | null>(identity, "CanNotifyKycOwner", "canNotifyKycOwner") ?? null,
        })),
        assignments: assignmentsRaw.map((assignment) =>
        ({
            id: readProp<number>(assignment, "Id", "id") as number,
            userIdentityId: readProp<number>(assignment, "UserIdentityId", "userIdentityId") as number,
            savingsPlanId: readProp<number>(assignment, "SavingsPlanId", "savingsPlanId") as number,
            relation: readProp<number>(assignment, "Relation", "relation") as number,
        })),
    };
}

function mapWireErrors(errors: unknown): OnboardingErrorItem[]
{
    if(!Array.isArray(errors))
    {
        return [];
    }

    return errors.map((e: any) =>
    ({
        code: (readProp<string>(e, "Code", "code") || "UNKNOWN") as string,
        message: readProp<string>(e, "Message", "message"),
    }));
}

function firstWireErrorMessage(responseData: any): string | undefined
{
    const errors = readProp<any[]>(responseData, "Errors", "errors");
    if(!errors || errors.length === 0)
    {
        return undefined;
    }

    return readProp<string>(errors[0], "Message", "message");
}

function isOnboardingEnvelopeBody(body: unknown): body is Record<string, unknown>
{
    return Boolean(body && typeof body === "object" && ("Success" in body || "success" in body));
}

export function parseOnboardingEnvelopeWire<TWire, TNorm>(body: unknown, normalize: (wire: TWire) => TNorm): OnboardingEnvelope<TNorm>
{
    if(isOnboardingEnvelopeBody(body))
    {
        const success = Boolean(readProp(body, "Success", "success"));
        const dataRaw = readProp<TWire | null>(body, "Data", "data");
        const errorsRaw = readProp(body, "Errors", "errors");

        return{
            success,
            data: dataRaw ? normalize(dataRaw) : null,
            errors: mapWireErrors(errorsRaw),
        };
    }

    return{
        success: false,
        data: null,
        errors: [{ code: "UNEXPECTED_RESPONSE", message: "Respuesta inesperada del servidor." }],
    };
}

export function mapAxiosErrorToOnboardingEnvelope<TNorm>(error: any, normalize?: (wire: any) => TNorm): OnboardingEnvelope<TNorm>
{
    const responseData = error?.response?.data;
    if(isOnboardingEnvelopeBody(responseData))
    {
        if(normalize)
        {
            return parseOnboardingEnvelopeWire(responseData, normalize);
        }

        return{
            success: false,
            data: null,
            errors: mapWireErrors(readProp(responseData, "Errors", "errors")),
        };
    }

    if(error?.response?.status === 404)
    {
        return{
            success: false,
            data: null,
            errors: [{ code: "NOT_FOUND", message: firstWireErrorMessage(responseData) }],
        };
    }

    if(error?.response?.status === 403)
    {
        return{
            success: false,
            data: null,
            errors: [{ code: "FORBIDDEN", message: firstWireErrorMessage(responseData) }],
        };
    }

    if(error?.response)
    {
        return{
            success: false,
            data: null,
            errors: [
            {
                code: "HTTP_ERROR",
                message: firstWireErrorMessage(responseData) || error.message,
            }],
        };
    }

    return{
        success: false,
        data: null,
        errors: [{ code: "NETWORK_ERROR", message: error?.message || "Error de red." }],
    };
}